import React, {Fragment} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

// Components
import Modal from "../../components/Modal.js";

// Style
import {pad} from "../../style/components/variables.js";
import {flex} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {breakpoint} from "../../style/components/breakpoints.js";
import {Button, HeadingCenter, Small} from "../../style/components/general.js";

const ModalRedirect = ({
  visible,
  setVisible,
  buttonOptions,
  orientation = "row",
  goBack,
  hasBackButton = false,
  title,
  shouldHide = true
}) => (
  <Modal
    visible={visible}
    setVisible={setVisible}
    maxWidth={breakpoint.width[1]}
    hasBackButton={hasBackButton}
    goBack={goBack}>
    {title && <HeadingCenter>{title}</HeadingCenter>}
    <Options orientation={orientation}>
      {buttonOptions.map(({action, description, elaboration, icon, disabledAction}) => (
        <Fragment key={description}>
          {elaboration && (
            <Small center secondary>
              {elaboration}
            </Small>
          )}
          <Option
            isDisabled={!!disabledAction}
            onClick={() => {
              if (disabledAction) disabledAction();
              else {
                action();
                if (goBack) goBack();
                else if (shouldHide) setVisible(false);
              }
            }}>
            <OptionLabel>
              {icon && <FontAwesomeIcon icon={icon} />}
              <Small>{description}</Small>
            </OptionLabel>
          </Option>
        </Fragment>
      ))}
    </Options>
  </Modal>
);

ModalRedirect.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  buttonOptions: PropTypes.arrayOf(
    PropTypes.shape({
      action: PropTypes.func.isRequired,
      description: PropTypes.string.isRequired,
      elaboration: PropTypes.string,
      icon: PropTypes.any,
      disabledAction: PropTypes.func
    })
  ).isRequired,
  orientation: PropTypes.string,
  goBack: PropTypes.func,
  hasBackButton: PropTypes.bool,
  title: PropTypes.string,
  shouldHide: PropTypes.bool
};

// Style Overrides
const Options = styled.div`
  display: ${({orientation}) => (orientation === "column" ? "flex" : "grid")};
  gap: ${pad}px;

  ${({orientation}) =>
    orientation === "column"
      ? css`
          flex-direction: column;
        `
      : css`
          grid-template-columns: repeat(2, minmax(0, 1fr));
        `}
`;

const Option = styled.div`
  align-self: stretch;
  width: 100%;
  min-width: 0;

  ${({isDisabled}) =>
    isDisabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}
`;

const OptionLabel = styled(Button)`
  ${flex("column", "wrap", "center", "center")};
  ${voice.medium};
  width: 100%;
  height: 100%;
  padding: ${pad}px;
  padding-top: 12px;

  text-align: center;
  text-transform: uppercase;
  color: ${props => props.theme.tertiary};
  background-color: ${props => props.theme.secondary};

  svg {
    fill: ${props => props.theme.tertiary};
    margin-bottom: ${pad}px;
  }
`;

export default ModalRedirect;
